import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    //TODO: uncoment when 2FA with firebase is enabled
    /* 
    RecaptchaVerifier,
    multiFactor,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    */
    UserCredential,
} from "firebase/auth";

import { initializeApp } from "firebase/app";
import { MESSAGES_MAP } from "./i18n";
import { LabSignUpFormData, TApiLab } from "../lib/types/labs";
import { useAuthContext } from "../contexts/auth-context";
import { apiRequestSecureGet, apiRequestSecuredDelete, apiRequestSecuredPatch, apiRequestSecuredPost } from "../lib/common/api";
import { TUserInfo } from "../lib/types/users";
import { RouteEnumType } from "../lib/types/routes";
import { useRouteNavigation } from "../lib/common/routes";
import { MouseEvent } from "react";
import { DentalTechSignUpFormData, TApiTechnician } from "../lib/types/dentalTechs";
import { useServiceContext } from "../contexts/service";

// TODO: Replace the following with your app's Firebase project configuration
// See: https://firebase.google.com/docs/web/learn-more#config-object
const firebaseConfig = {
    apiKey: "AIzaSyDTJBqOrXYSRxxGBuj2aWPBLqYS3GDM0Oc",
    authDomain: "winwingo-firebase.firebaseapp.com",
    projectId: "winwingo-firebase",
    storageBucket: "winwingo-firebase.appspot.com",
    messagingSenderId: "606419034082",
    appId: "1:606419034082:web:814ec5acd5bf36a23723bb",
};

const app = initializeApp(firebaseConfig);

export const getFirebaseAppAuth = () => {
    return getAuth(initializeApp(firebaseConfig));
};

export type LabUserResponse = {
    guid: string;
    error?: string;
};

export const useUserService = () => {
    const { setIsLoggedIn, getUserTokenId, setUserTokenId, isLoggedIn, userInfo, logInUser } = useAuthContext();
    const {
        general: { logOut },
    } = useServiceContext();

    const URLS = {
        addUser: "users/add",
    };

    const { navigateToRoute, navigateRouteClickHandler, getRoute } = useRouteNavigation();

    let currentUserTokenId = "";

    const signUpUser = async (email: string, pwd: string): Promise<LabUserResponse> => {
        // Initialize Firebase

        // Initialize Firebase Authentication and get a reference to the service
        const auth = getAuth(app);
        let response: LabUserResponse = {
            guid: "",
        };
        try {
            const resp = await createUserWithEmailAndPassword(auth, email, pwd);
            setIsLoggedIn(true);
            currentUserTokenId = await resp.user.getIdToken();
            setUserTokenId(currentUserTokenId);
            response.guid = resp.user.uid;
        } catch (error: any) {
            response.error = MESSAGES_MAP[error.code];
        }
        return response;
    };

    const configureNewLab = async (data: LabSignUpFormData) => {
        await apiRequestSecuredPost(URLS.addUser, currentUserTokenId || (await getUserTokenId()), {
            user: {
                name: data.labName,
                guid: data.guid,
                type: "lab",
                business_type_id: data.business_type_id,
                other: {
                    ...data,
                    business_type_id: undefined,
                    positions: (data.positions || []).map((p) => p.id),
                    password: undefined,
                },
            },
        });
        const userData = await getUserInfo();
        logInUser(currentUserTokenId || (await getUserTokenId()), userData);
    };

    const configureNewTech = async (data: DentalTechSignUpFormData) => {
        await apiRequestSecuredPost(URLS.addUser, currentUserTokenId || (await getUserTokenId()), {
            user: {
                name: data.firstName + " " + data.lastName,
                guid: data.guid,
                type: "tech",
                other: { ...data, password: undefined },
            },
        });
        const userData = await getUserInfo();
        logInUser(currentUserTokenId || (await getUserTokenId()), userData);
    };

    const updateLab = async (data: LabSignUpFormData, name?: string) => {
        await apiRequestSecuredPatch(URLS.addUser, currentUserTokenId || (await getUserTokenId()), {
            name,
            data,
        });
    };

    // const updateTech = async (
    //     data: DentalTechSignUpFormData,
    //     name?: string
    // ) => {
    //     await apiRequestSecuredPatch(
    //         URLS.addUser,
    //         currentUserTokenId || (await getUserTokenId()),
    //         {
    //             name,
    //             data,
    //         }
    //     );
    // };

    const updateTechAPI = async (data: TApiTechnician, name?: string) => {
        await apiRequestSecuredPatch(URLS.addUser, currentUserTokenId || (await getUserTokenId()), {
            name,
            data: data.other,
            education: data.education || { education: [] },
            experience: data.experience || { experience: [] },
        });
    };

    const UserTypeRedirects: {
        [key: string]: RouteEnumType;
    } = {
        lab: "dashboardLab",
        tech: "dashboardTech",
        admin: "adminDashboard",
    };

    const singInUser = async (
        email: string,
        pwd: string,
    ): Promise<{
        userInfo: TUserInfo;
        fbUserInfo: UserCredential;
    }> => {
        const auth = getAuth(app);
        const fbUserInfo = await signInWithEmailAndPassword(auth, email, pwd);

        currentUserTokenId = await fbUserInfo.user.getIdToken();
        await apiRequestSecureGet<any>("users/login", currentUserTokenId);
        const userInfo = await getUserInfo();

        logInUser(currentUserTokenId, userInfo);

        navigateToRoute(UserTypeRedirects[userInfo.type]);

        return { userInfo, fbUserInfo };
    };

    const resetPassword = async (email: string): Promise<boolean | void> => {
        const actionCodeSettings = {
            url: `${window.location.origin}/__/auth/action`,
            handleCodeInApp: true,
        };

        try {
            const auth = getAuth(app);
            const message = await sendPasswordResetEmail(auth, email, actionCodeSettings)
                .then((data) => {
                    if (data === undefined) {
                        return true;
                    }
                })
                .catch((error) => {
                    if (error.code === "auth/user-not-found") {
                        return false;
                    }
                });
            return message;
        } catch (error: any) {
            return error.code;
        }
    };

    const getUserInfo = async (): Promise<TUserInfo> => {
        const respUserInfo = await apiRequestSecureGet<TUserInfo>("users/info", currentUserTokenId || (await getUserTokenId()));

        return respUserInfo.data;
    };

    const refreshSuprSendUserToken = async (): Promise<string> => {
        const refreshedUserInfo = await getUserInfo();
        return refreshedUserInfo.suprSend.userToken;
    };

    const removeFromMyContactList = async (contactUserId: number): Promise<any> => {
        return apiRequestSecuredDelete("users/removecontact", currentUserTokenId || (await getUserTokenId()), { contactUserId: contactUserId });
    };

    const getAnyUserInfo = async (userId: number): Promise<TApiTechnician> => {
        const respUserInfo = await apiRequestSecureGet<TApiTechnician>("users/infobyid", currentUserTokenId || (await getUserTokenId()), {
            userid: userId,
        });

        return respUserInfo.data;
    };

    const navigateToDashboardClickHandler = (e: MouseEvent) => {
        e.preventDefault();

        if (userInfo && isLoggedIn) {
            const section: RouteEnumType = userInfo.type === "lab" ? "dashboardLab" : "dashboardTech";

            navigateRouteClickHandler(section, e);
        }
    };
    const navigateToMyAccountClickHandler = (e: MouseEvent) => {
        e.preventDefault();

        if (userInfo && isLoggedIn) {
            const section: RouteEnumType = userInfo.type === "lab" ? "dentalLabAccount" : "dentalTechAccount";

            navigateRouteClickHandler(section, e);
        }
    };

    const navigateToMyHomeClickHandler = (e: MouseEvent) => {
        e.preventDefault();
        const section: RouteEnumType = !userInfo || !isLoggedIn ? "home" : "homeRegistered";

        navigateRouteClickHandler(section, e);
    };

    const getDashboardRoute = (): string => {
        if (!userInfo || !isLoggedIn) return "home";

        return getRoute(userInfo.type === "lab" ? "dashboardLab" : "dashboardTech");
    };

    const getHomeRoute = (): string => {
        if (!userInfo || !isLoggedIn) return "home";
        return getRoute("homeRegistered");
    };

    const getMyAccountRoute = (): string => {
        if (!userInfo || !isLoggedIn) return "home";

        return getRoute(userInfo.type === "lab" ? "dentalLabAccount" : "dentalTechAccount");
    };

    /**
     * generic method to convert a technician to a common UserInfo type used
     * to show common info between tech and labs
     * @param data
     * @param name
     * @returns TUserInfo
     */
    const techAPI2UserINfo = (data: TApiTechnician, name?: string): TUserInfo => {
        return {
            id: data.id,
            guid: data.guid,
            images: data.images,
            name: name || data.name,
            type: "tech",
            enabled: data.enabled,
            suprSend: userInfo!.suprSend,
            other: {
                phone: data.other.phone,
                desiredRegion: data.other.desiredRegion,
                desiredSalary: { amount: data.other.desiredSalaryPerYear.amount, frequency: data.other.desiredSalaryPerYear.frequency },
                hobbies: data.other.hobbies,
                interests: data.other.interests,
                roles: data.other.roles,
                yearsOfExperience: data.other.yearsOfExperience,
                imgUrl: data.other.imgUrl,
                logoCustomization: data.other.logoCustomization,
            },
            incognito: data.incognito,
            verifications: data.verifications,
        };
    };

    const LabSignUpFormData2UserInfo = (data: LabSignUpFormData, labInfo: TApiLab, name?: string): TUserInfo => {
        const phoneChanged = data.phone !== labInfo.other.phone;

        return {
            id: labInfo.id,
            guid: labInfo.guid,
            name: name || data.labName || labInfo.name,
            other: {
                desiredRegion: data.desiredRegion as string[],
                desiredSalary: { amount: data.desiredSalaryPerYear?.amount as number, frequency: data.desiredSalaryPerYear?.frequency },
                hobbies: data.hobbies as string[],
                interests: data.interests as string[],
                roles: [], //TODO: angel - mejorar esto cuando estén definidos los roles para los laboratorios:  data.positions as string[],
                yearsOfExperience: data.yearsInBusiness as number,
                imgUrl: "",
                logoCustomization: labInfo.other.logoCustomization,
                phone: data.phone,
            },
            type: "lab",
            images: { ...labInfo.images },
            enabled: true,
            suprSend: userInfo!.suprSend,
            incognito: false,
            verifications: {
                phone: phoneChanged ? false : labInfo.verifications?.phone,
                email: labInfo.verifications?.email,
            },
        };
    };

    const reportUser = async (userId: number, reason: string): Promise<string> => {
        try {
            await apiRequestSecuredPost("users/report", await getUserTokenId(), {
                reported_user_id: userId,
                reason,
            });
        } catch (error: any) {
            if (error?.response?.data) {
                const errorMessage = error.response.data.error as string;
                switch (errorMessage) {
                    case "Already reported by this user":
                        return "You already reported this user";
                    case "Disabled user":
                        return "This user is already disabled, you can't report it.";
                    default:
                        throw error;
                }
            } else throw error;
        }

        return "ok";
    };

    const addLabToBlacklist = async (lab_id: number): Promise<boolean> => {
        try {
            await apiRequestSecuredPost("users/blacklist", currentUserTokenId || (await getUserTokenId()), { lab_id });
        } catch (err) {
            console.error(err);
        }
        return true;
    };

    const deleteLabBlacklist = async (lab_id: number): Promise<boolean> => {
        try {
            await apiRequestSecuredDelete("users/deleteblacklist", currentUserTokenId || (await getUserTokenId()), { lab_id: lab_id });
        } catch (error) {
            console.error(error);
        }
        return true;
    };
    //TODO:uncomment when implementing 2FA from firebase
    /* 
    const multiFactorProcess = async (pButton: HTMLElement) => { //2FA
        try {
            const _auth = getAuth(app);
            const user = _auth.currentUser;
            if (!user) throw new Error("useUseService -> multiFactorProcess -> no current user");

            const recaptchaVerifier = new RecaptchaVerifier(pButton, { size: "invisible" }, _auth);
            const multifactorSession = await multiFactor(user).getSession();
            const phoneInfoOptions = {
                phoneNumber: "+5493415473805",
                session: multifactorSession,
            };
            const phoneAuthProvider = new PhoneAuthProvider(_auth);
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);

            let verificationCode = "something";
            const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
            const multiFactorAssertion = await PhoneMultiFactorGenerator.assertion(cred);
            await multiFactor(user).enroll(multiFactorAssertion, "My personal phone number");
        } catch (error) {

            console.log("ERROR: ", error);
        }
    };
*/
    return {
        configureNewLab,
        configureNewTech,
        logOutUser: () => {
            getAuth(app).signOut();
            logOut();
            navigateToRoute("home");
        },
        logOutUserDueToInactivity: () => {
            getAuth(app).signOut();
            logOut();
            navigateToRoute("logoutduetoinactivity");
        },
        //TODO: uncomment when 2FA is enabled with firebae
        //multiFactorProcess,
        //recaptchaVerifier,
        reportUser,
        singInUser,
        signUpUser,
        isLoggedIn,
        userInfo,
        navigateToMyHomeClickHandler,
        navigateToDashboardClickHandler,
        navigateToMyAccountClickHandler,
        updateLab,
        //updateTech,
        updateTechAPI,
        getHomeRoute,
        getDashboardRoute,
        getMyAccountRoute,
        refreshSuprSendUserToken,
        getAnyUserInfo,
        removeFromMyContactList,
        techAPI2UserINfo,
        LabSignUpFormData2UserInfo,
        resetPassword,
        addLabToBlacklist,
        deleteLabBlacklist,
    };
};
